import React, { useEffect, useState, useCallback, forwardRef } from "react";

import { Row, Col, Dropdown } from "react-bootstrap";
import ReactPaginate from "react-paginate";
import { useNavigate, useLocation } from "react-router-dom";
import { Toast, ToastContainer } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

//Context & Permissions
import { useGlobal } from "../../GlobalContext";
import { canCreate, canEdit, canDelete, canView } from "../common/permissionUtils";

//API Imports

//SVG Images
import Search from "../../assets/svg/search.svg";
import CalendarIcon from "../../assets/svg/calendar.svg";
import Plus from "../../assets/svg/add.svg";
import Dots from "../../assets/svg/groupdots.svg";
import Customize from "../../assets/svg/customize.svg";
import lar from "../../assets/svg/lar.svg";
import rar from "../../assets/svg/rar.svg";
import { ReactComponent as EditRow } from "../../assets/svg/edit.svg";
import { ReactComponent as DeleteRow } from "../../assets/svg/delete.svg";
import { ReactComponent as QuotationIc } from "../../assets/svg/quotation_ic.svg";
import { ReactComponent as ApprovIc } from "../../assets/svg/approv_ic.svg";
import { ReactComponent as PendingIc } from "../../assets/svg/pending_ic.svg";
import { ReactComponent as RejectIc } from "../../assets/svg/reject_ic.svg";
import { ReactComponent as NegoitateIc } from "../../assets/svg/negoitate_ic.svg";
import Copy from "../../assets/svg/copy_ics_a.svg";
import DeleteModal from "../common/delete-multi.js";
import { sanitizeInput } from "../common/security-utils.js";
import RecordsPerPage from "../common/RecordsPerPage.jsx";
import SearchWithAPI from "../common/search-box-new";
import Loader from "../common/Loader.jsx";
import {
  bulkDeleteQuotationApi,
  deleteQuotationApi,
  getQuotationListApi,
} from "./quotation-api.js";
import "./quotation.css";
import AiChat from "../common/ai-chat.js";
import { askQuotationChatApi, getQuotationAiProvidersApi } from "./quotation-api";

// Persist list UI state (search, pagination, date range, filters) across
// navigation to a view page and back. Uses sessionStorage so it survives
// refresh but clears when the browser tab is closed.
const QUOTATION_LIST_STATE_KEY = "quotationListState";

const loadPersistedState = () => {
  try {
    const raw = sessionStorage.getItem(QUOTATION_LIST_STATE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const QuotationList = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const persisted = loadPersistedState();

  const [selectedStatus, setSelectedStatus] = useState(
    persisted.selectedStatus ?? { value: "", label: "All" },
  );
  // Customer filter (searchable dropdown from get_customers)
  const [customerFilter, setCustomerFilter] = useState(persisted.customerFilter ?? null);

  // Date range filter (From date - To date), shown as a two-month calendar
  const [startDate, setStartDate] = useState(
    persisted.startDate ? new Date(persisted.startDate) : null,
  );
  const [endDate, setEndDate] = useState(
    persisted.endDate ? new Date(persisted.endDate) : null,
  );
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const formatYearMonthDay = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const handleDateChange = (dates) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
    setItemOffset(0);
    if (start && end) {
      setIsDatePickerOpen(false);
    }
  };

  const CalendarCustomInput = forwardRef(({ onClick }, ref) => (
    <button
      type="button"
      ref={ref}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDatePickerOpen((prev) => !prev);
      }}
      title="Filter by Date"
      style={{
        width: "40px",
        height: "40px",
        borderRadius: "50%",
        border: "1px solid #e5e5e5",
        background: startDate ? "#3D4AF5" : "#ffffff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        transition: "all 0.2s ease",
        flexShrink: 0,
        boxSizing: "border-box",
        padding: 0,
        margin: 0,
        outline: "none",
      }}
    >
      <img
        src={CalendarIcon}
        alt="Calendar"
        style={{
          width: "18px",
          height: "18px",
          display: "block",
          margin: "auto",
          filter: startDate ? "brightness(0) invert(1)" : "none",
          flexShrink: 0,
        }}
      />
    </button>
  ));

  const options = [
    "QUOTATION NO",
    "TITLE",
    "CUSTOMER",
    "CONTACT PERSON",
    "VALIDITY DATE",
    "AMOUNT",
    "DATE CREATED",
    "STATUS",
  ];

  //Inputs
  const [isBulkDelete, setIsBulkDelete] = useState(false);
  const [statusOption] = useState([
    {
      value: "",
      label: "All",
    },
    { value: "Draft", label: "Draft" },
    { value: "Approved", label: "Approved" },    
    { value: "Sent", label: "Sent" },
    { value: "Negotiate", label: "Negotiate" },
    { value: "Customer Accepted", label: "Customer Accepted" },
    { value: "Customer Rejected", label: "Customer Rejected" },
    { value: "Sale Order", label: "Sale Order" },
    { value: "Rejected", label: "Rejected" },
    { value: "Expired", label: "Expired" },
  ]);

  //Hovered Index
  const [hoveredIndex, setHoveredIndex] = useState(null);

  //Navigate
  const navigate = useNavigate();
  const location = useLocation();

  // 🔒 Permissions
  const { rolePermissionList } = useGlobal();
  const hasCreatePermission = canCreate(rolePermissionList, "Quotation");
  const hasEditPermission = canEdit(rolePermissionList, "Quotation");
  const hasDeletePermission = canDelete(rolePermissionList, "Quotation");
  const hasViewPermission = canView(rolePermissionList, "Quotation");

  //Constants
  const MAX_SEARCH_LENGTH = 200;
  const MAX_ITEMS_PER_PAGE = 100;

  //Search
  const [search, setSearch] = useState(persisted.search ?? "");

  //Search handler with sanitization
  const handleSearchChange = (e) => {
    const rawValue = e.target.value;
    // Limit search length to prevent DoS
    if (rawValue.length > MAX_SEARCH_LENGTH) {
      return;
    }
    // Sanitize input to prevent XSS
    const sanitized = sanitizeInput(rawValue);
    setSearch(sanitized);
  };

  //Customize Table Dropdown
  const [showModal, setShowModal] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState(() =>
    Object.fromEntries(options.map((label) => [label, true])),
  );

  const toggleColumn = (label) => {
    setSelectedColumns((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };
  const anyColumnSelected = Object.values(selectedColumns).some(Boolean);
  // The header and each data row are separate flex containers (this isn't a
  // real <table>), so flexbox alone can't guarantee their columns line up —
  // each row computes its own flex distribution independently. CSS Grid with
  // an identical grid-template-columns shared by the header and every row
  // forces the same column boundaries everywhere.
  const visibleOptions = options.filter((label) => selectedColumns[label]);
  // Quotation No. / Status keep their previous 1.5x weighting relative to
  // the other columns' 1x.
  const columnWeight = (label) => (label === "QUOTATION NO" || label === "STATUS" ? 1.5 : 1);
  const columnsGridTemplate = visibleOptions.map((label) => `${columnWeight(label)}fr`).join(" ") || "1fr";

  // Client-side sort — sorts only the currently loaded page of rows, no API
  // involvement. sortColumn is one of `options`; sortDirection toggles asc/desc;
  // clicking a different column resets to asc.
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  const handleSortClick = (label) => {
    if (sortColumn === label) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(label);
      setSortDirection("asc");
    }
  };

  //Delete
  const [deleteId, setDeleteId] = useState(null);
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  useEffect(() => {
    if (deleteId != null) {
      setIsOpenDelete(true);
    }
  }, [deleteId]);

  //Pagination
  const [itemOffset, setItemOffset] = useState(persisted.itemOffset ?? 0);
  const [itemsPerPage, setItemsPerPage] = useState(persisted.itemsPerPage ?? 10);
  const [pageCount, setPageCount] = useState(0);
  const [records, setRecords] = useState(0);

  const handlePageClick = useCallback((event) => {
    setItemOffset(event.selected);
  }, []);

  //Get Leads List
  const [quotationList, setQuotationList] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [totalValue, setTotalValue] = useState({});
  const [approveValue, setApproveValue] = useState({});
  const [pendingValue, setPendingValue] = useState({});
  const [rejectedValue, setRejectedValue] = useState({});
  const [negotiatedValue, setNegotiatedValue] = useState({});

  const getQuotationList = useCallback(async () => {
    const params = {
      search: search,
      limit: itemsPerPage,
      skip: itemOffset * itemsPerPage,
      status: selectedStatus.value ?? "",
      customer_id: customerFilter?.value ?? "",
      ...(startDate
        ? { from_date: formatYearMonthDay(startDate), to_date: formatYearMonthDay(endDate || startDate) }
        : {}),
    };
    setListLoading(true);
    try {
      const response = await getQuotationListApi(params);
      if (response.data.success === true) {
        setPageCount(response.data.total_pgs);
        setRecords(response.data.total_rec);
        setTotalValue(response.data.total || {});
        setApproveValue(response.data.approved || {});
        setPendingValue(response.data.pending || {});
        setRejectedValue(response.data.rejected || {});
        setNegotiatedValue(response.data.negotiated || {});
        const formatted = formatQuotationData(response.data.quotations);
        setQuotationList(formatted);
        setSelectedRows([]);
        setAllSelected(false);
      }
      return response.data;
    } catch (err) {
      // Error handled silently
    } finally {
      setListLoading(false);
    }
  }, [search, itemsPerPage, selectedStatus, itemOffset, customerFilter, startDate, endDate]);

  useEffect(() => {
    getQuotationList();
  }, [getQuotationList]);

  // Sort only the currently loaded page — client-side, no refetch.
  const sortedQuotationList = React.useMemo(() => {
    if (!sortColumn) return quotationList;
    const dir = sortDirection === "asc" ? 1 : -1;
    return [...quotationList].sort((a, b) => {
      if (sortColumn === "AMOUNT") {
        const aNum = Number(a[sortColumn]) || 0;
        const bNum = Number(b[sortColumn]) || 0;
        return (aNum - bNum) * dir;
      }
      const aStr = (a[sortColumn] ?? "").toString().toLowerCase();
      const bStr = (b[sortColumn] ?? "").toString().toLowerCase();
      return aStr.localeCompare(bStr) * dir;
    });
  }, [quotationList, sortColumn, sortDirection]);

  // When the Quotation sidebar menu is clicked it navigates with { resetList: true }.
  // If we're already on this page (no remount), reset live state to defaults so the
  // menu click always gives a fresh list. The flag is cleared so a refresh/back
  // doesn't re-trigger the reset.
  useEffect(() => {
    if (location.state?.resetList) {
      setSearch("");
      setItemOffset(0);
      setItemsPerPage(10);
      setStartDate(null);
      setEndDate(null);
      setSelectedStatus({ value: "", label: "All" });
      setCustomerFilter(null);
      sessionStorage.removeItem(QUOTATION_LIST_STATE_KEY);
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Persist list UI state so it is restored when returning from a view page.
  useEffect(() => {
    try {
      sessionStorage.setItem(
        QUOTATION_LIST_STATE_KEY,
        JSON.stringify({
          search,
          itemOffset,
          itemsPerPage,
          startDate: startDate ? startDate.toISOString() : null,
          endDate: endDate ? endDate.toISOString() : null,
          selectedStatus,
          customerFilter,
        }),
      );
    } catch {
      // ignore storage write errors (e.g. private mode quota)
    }
  }, [search, itemOffset, itemsPerPage, startDate, endDate, selectedStatus, customerFilter]);

  useEffect(() => {
    setSelectedRows([]);
    setAllSelected(false);
  }, [search, itemsPerPage, selectedStatus, itemOffset, selectedColumns, customerFilter, startDate, endDate]);

  useEffect(() => {
    return () => {
      setSelectedRows([]);
      setAllSelected(false);
    };
  }, []);

  const formatQuotationData = (rawQuotations) => {
    return rawQuotations.map((quotation) => ({
      id: quotation.id,
      "QUOTATION NO": quotation.quotation_id,
      "TITLE": quotation.title,
      CUSTOMER: quotation.customer,
      "CONTACT PERSON": quotation.contact_person,
      "VALIDITY DATE": quotation.valid_date,
      AMOUNT: quotation.amount,
      "DATE CREATED": quotation.created,
      STATUS: quotation.status,
      is_expired: quotation.is_expired,
    }));
  };

  // state
  const [selectedRows, setSelectedRows] = useState([]);
  const [allSelected, setAllSelected] = useState(false);

  const normalizeId = (id) =>
    id === null || id === undefined ? id : String(id);

  useEffect(() => {
    const selectable = quotationList.filter((q) => q.STATUS === "Draft");
    if (selectable.length > 0) {
      setAllSelected(selectable.every((item) => selectedRows.includes(normalizeId(item.id))));
    } else {
      setAllSelected(false);
    }
  }, [selectedRows, quotationList]);

  // handle checkbox logic
  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedRows([]);
    } else {
      const selectableIds = quotationList
        .filter((q) => q.STATUS === "Draft")
        .map((q) => normalizeId(q.id));
      setSelectedRows(selectableIds);
    }
  };

  const toggleRow = (id) => {
    const normalId = normalizeId(id);
    setSelectedRows((prev) =>
      prev.includes(normalId) ? prev.filter((item) => item !== normalId) : [...prev, normalId]
    );
  };

  //Snackbar
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarStatus, setSnackbarStatus] = useState(false);
  const handleSnackbarShow = () => setShowSnackbar(true);
  const handleSnackbarClose = () => setShowSnackbar(false);

  // Copy to clipboard function
  const handleCopyQuotationNo = (quotationNo) => {
    navigator.clipboard.writeText(quotationNo).then(() => {
      setSnackbarMessage(`✓ Copied: ${quotationNo}`);
      setSnackbarStatus(true);
      handleSnackbarShow();
    }).catch(() => {
      setSnackbarMessage("✗ Failed to copy");
      setSnackbarStatus(false);
      handleSnackbarShow();
    });
  };

  const summaryData = [
    {
      label: "Quotations",
      count: totalValue?.count || 0,
      value: totalValue?.amount || "-",
      icon: <QuotationIc />,
      bg: "primary",
    },
    {
      label: "Approved",
      count: approveValue?.count || 0,
      value: approveValue?.amount || "-",
      icon: <ApprovIc />,
      bg: "success",
    },
    {
      label: "Draft",
      count: pendingValue?.count || 0,
      value: pendingValue?.amount || "-",
      icon: <PendingIc />,
      bg: "warning",
    },
    {
      label: "Rejected",

      count: rejectedValue?.count || 0,
      value: rejectedValue?.amount || "-",

      icon: <RejectIc />,
      bg: "danger",
    },
    {
      label: "Negotiated",

      count: negotiatedValue?.count || 0,
      value: negotiatedValue?.amount || "-",
      icon: <NegoitateIc />,
      bg: "info",
    },
  ];
  const deleteSubIndustry = async () => {
    try {
      // 🔒 Permission check for delete
      if (!hasDeletePermission) {
        setSnackbarMessage("You don't have permission to delete quotations");
        setSnackbarStatus(false);
        handleSnackbarShow();
        return;
      }

      if (isBulkDelete) {
        if (!selectedRows || selectedRows.length === 0) {
          setSnackbarMessage("No records selected for bulk delete.");
          setSnackbarStatus(false);
          handleSnackbarShow();
          return;
        }
        const response = await bulkDeleteQuotationApi(selectedRows);
        if (response?.data?.success) {
          setSnackbarMessage(
            sanitizeInput(response.data.message || "Bulk delete successful"),
          );
          setSnackbarStatus(true);
          handleSnackbarShow();
          setSelectedRows([]);
          setIsBulkDelete(false);
          setIsOpenDelete(false);
          getQuotationList();
        } else {
          setSnackbarMessage(
            sanitizeInput(response?.data?.message || "Failed bulk delete"),
          );
          setSnackbarStatus(false);
          handleSnackbarShow();
        }
      } else {
        if (!deleteId) return;
        const response = await deleteQuotationApi(deleteId);
        if (response?.data?.success) {
          setSnackbarMessage(
            sanitizeInput(response.data.message || "Deleted successfully"),
          );
          setSnackbarStatus(true);
          handleSnackbarShow();
          setDeleteId(null);
          setIsOpenDelete(false);
          getQuotationList();
        } else {
          setSnackbarMessage(
            sanitizeInput(response?.data?.message || "Failed to delete"),
          );
          setSnackbarStatus(false);
          handleSnackbarShow();
        }
      }
    } catch (err) {
      setSnackbarMessage("Error deleting record.");
      setSnackbarStatus(false);
      handleSnackbarShow();
    }
  };
  return (
    <div
      style={{
        fontFamily: "'Euclid Circular B', sans-serif",
        padding: "16px",
        height: "100vh",
        // width: "83vw",
        overflowX: "hidden",
        backgroundColor: "#f8f9fb",
      }}
    >
      <DeleteModal
        isOpen={isOpenDelete}
        title={
          selectedRows.length > 0 ? `selected (${selectedRows.length}) quotations` : "quotation"
        }
        count={selectedRows.length}
        onClose={() => {
          setIsOpenDelete(false);
          setDeleteId(null);
          setIsBulkDelete(false);
          setSelectedRows([]);
          setAllSelected(false);
        }}
        onDelete={deleteSubIndustry}
      />
      <div style={{ marginBottom: "12px" }}>
        <div className="d-flex flex-wrap gap-3">
          {summaryData.map((item, index) => (
            <div
              className="flex-fill bg-white"
              key={index}
              style={{
                minWidth: "180px",
                maxWidth: "200px",
                height: "77px",
                borderRadius: "12px",
                flex: "1",
              }}
            >
              <div
                className={` bg-white h-100 `}
                style={{
                  borderRadius: "12px",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "start",
                  textAlign: "start",
                }}
              >
                <div
                  className="card-body d-flex align-items-center justify-content-start"
                  style={{ paddingLeft: "12px" }}
                >
                  <div
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                      backgroundColor: "#F2F4FF",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      marginRight: "10px", // space between icon and text
                    }}
                  >
                    {item.icon}
                  </div>
                  <div className="d-flex flex-column align-items-start">
                    <div className="fw-600 fs-14 text-grey mb-2">
                      {item.label}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px", // space between number and dot
                      }}
                    >
                      <span className="fw-600 fs-20 textcolor">
                        {item.count}
                      </span>
                      <div
                        style={{
                          width: "4px",
                          height: "4px",
                          borderRadius: "50%",
                          backgroundColor: "#D9D9D9",
                        }}
                      ></div>
                      <div
                        className="fs-13 fw-500 textblue"
                        style={{
                          paddingRight: "12px",
                          paddingLeft: "12px",
                          borderRadius: "100px",
                          backgroundColor: "#EFF0FF",
                        }}
                      >
                        ₹ {item.value ?? 0}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div
        className="bg-white"
        style={{ borderRadius: "16px", padding: "12px" }}
      >
        <Row className="align-items-center justify-content-between g-3 mb-2">
          {/* Left Section — Label + Filter */}
          <Col
            xs={12}
            md={6}
            lg={4}
            className="d-flex align-items-center gap-3"
          >
            <div
              className="fs-14 fw-600 text-dkblue"
              style={{
                /* borderRight: "1.5px solid #E0E0E0", */
                paddingRight: "12px",
              }}
            >
              Quotation - {records}
            </div>

            {/* Example Filter Dropdown */}
            {/* <div
              style={{
                width: "36px",
                height: "36px",
                borderRadius: "100px",
                border: "0.6px solid #E5E5E5",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FilterIc />
            </div> */}
          </Col>

          {/* Right Section — Search + Filters + Three-dot Dropdown */}
          <Col
            xs={12}
            md={6}
            lg={8}
            className="d-flex align-items-center justify-content-end gap-3 flex-wrap"
          >
            {/* Search Box */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                borderRadius: "100px",
                border: "0.8px solid #e5e5e5",
                padding: "9px 16px",
                backgroundColor: "#ffffff",
                height: "40px",
                flex: 1,
                maxWidth: "250px",
              }}
            >
              <img src={Search} alt="search" style={{ width: "16px" }} />
              <input
                type="search"
                placeholder="Search..."
                value={search}
                onChange={handleSearchChange}
                maxLength={MAX_SEARCH_LENGTH}
                style={{
                  border: "none",
                  outline: "none",
                  fontSize: "14px",
                  flex: 1,
                  backgroundColor: "transparent",
                }}
                className="no-focus-border"
              />
            </div>

            {/* Status filter */}
            <Dropdown>
              <Dropdown.Toggle
                variant="light"
                id="dropdown-sort"
                className="d-flex align-items-center justify-content-between"
                style={{
                  borderRadius: "12px",
                  padding: "6px 12px",
                  backgroundColor: "#F6F6F6",
                }}
              >
                <span className="fs-12 fw-400 text-dkblue">Status :</span>
                <span className="d-flex align-items-center px-1">
                  <span className="fs-13 fw-500 textblue">
                    {selectedStatus?.label || "Select"}
                  </span>
                </span>
              </Dropdown.Toggle>

              <Dropdown.Menu
                style={{
                  backgroundColor: "white",
                  borderRadius: "8px",
                  border: "1px solid #EBEBEB",
                  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                }}
              >
                {statusOption.map((option) => (
                  <Dropdown.Item
                    className="fs-14 fw-500 textcolor"
                    key={option.value}
                    onClick={() => setSelectedStatus(option)}
                    active={selectedStatus?.value === option.value}
                  >
                    {option.label}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>

            {/* Customer filter */}
            <div style={{ width: "220px", flexShrink: 0, position: "relative" }}>
              <SearchWithAPI
                onDropdownChange={(option) => {
                  setCustomerFilter(option);
                  setItemOffset(0);
                }}
                apiUrl={apiUrl + "get_customers"}
                placeholder={"Filter by customer"}
                selectedOption={customerFilter}
              />
              {customerFilter?.value && (
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    setCustomerFilter(null);
                    setItemOffset(0);
                  }}
                  title="Clear customer filter"
                  style={{
                    position: "absolute",
                    right: "34px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    width: "18px",
                    height: "18px",
                    borderRadius: "50%",
                    background: "#e5e5e5",
                    color: "#555",
                    fontSize: "12px",
                    lineHeight: "18px",
                    textAlign: "center",
                    cursor: "pointer",
                    zIndex: 5,
                    userSelect: "none",
                  }}
                >
                  ✕
                </span>
              )}
            </div>

            {/* Date range filter — two-month calendar */}
            <div className="quotation-date-filter" style={{ width: "40px", height: "40px", flexShrink: 0, position: "relative" }}>
              <DatePicker
                selectsRange
                monthsShown={2}
                startDate={startDate}
                endDate={endDate}
                onChange={handleDateChange}
                isClearable
                customInput={<CalendarCustomInput />}
                calendarClassName="quotation-date-range-picker"
                popperPlacement="bottom-end"
                open={isDatePickerOpen}
                onClickOutside={() => setIsDatePickerOpen(false)}
              />
              {startDate && (
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    setStartDate(null);
                    setEndDate(null);
                    setItemOffset(0);
                    setIsDatePickerOpen(false);
                  }}
                  title="Clear date filter"
                  style={{
                    position: "absolute",
                    top: "-4px",
                    right: "-4px",
                    width: "18px",
                    height: "18px",
                    borderRadius: "50%",
                    background: "#ef4444",
                    color: "#ffffff",
                    fontSize: "11px",
                    lineHeight: "18px",
                    textAlign: "center",
                    cursor: "pointer",
                    zIndex: 10,
                    userSelect: "none",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                  }}
                >
                  ✕
                </span>
              )}
            </div>

            {selectedRows.length === 0 && !isBulkDelete && hasCreatePermission && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "10px",
                  padding: "0 20px",
                  borderRadius: "50px",
                  borderBottom: "3px solid #1723C2",
                  background:
                    "linear-gradient(180deg, #6971DD 0%, #3D4AF5 100%)",
                  height: "40px",
                  width: "auto", // allows full text width
                  whiteSpace: "nowrap", // prevents wrapping
                  cursor: "pointer",
                }}
                onClick={() => navigate("/quotation/create")}
              >
                <img src={Plus} style={{ width: "16px" }} alt="Plus" />
                <div className="fs-15 fw-600 text-white">Create Quotation</div>
              </div>
            )}
            {selectedRows.length > 0 && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "10px",
                  padding: "0 20px",
                  borderRadius: "50px",
                  borderBottom: "3px solid #B31717",
                  background:
                    "linear-gradient(180deg, #FF7A7A 0%, #D61916 100%)",
                  height: "40px",
                  width: "auto",
                  whiteSpace: "nowrap",
                  cursor: "pointer",
                }}
                onClick={() => {
                  setIsBulkDelete(true);
                  setIsOpenDelete(true);
                }}
              >
                <div className="fs-15 fw-600 text-white">Delete ({selectedRows.length})</div>
              </div>
            )}

            {/* Three-dot Dropdown */}
            {/* <div style={{ position: "relative" }}>
              <div
                style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "50%",
                  border: "0.6px solid #E5E5E5",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
                onClick={() => setOpen(!open)}
              >
                <ThreeDotIc className="fs-5" />
              </div>

              {open && (
                <div
                  style={{
                    position: "absolute",
                    right: 0,
                    top: "45px",
                    backgroundColor: "#ffffff",
                    padding: "10px 0",
                    borderRadius: "12px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    minWidth: "150px",
                    zIndex: 1000,
                  }}
                >
                  <div className="import-btn fs-14 fw-400 text-dkblue">
                    <ImportIc1 /> Import Data
                  </div>

                  <div
                    className="fs-14 fw-400 text-dkblue import-btn"
                    style={{ padding: "8px 16px", cursor: "pointer" }}
                  >
                    <DownIc /> Export CSV
                  </div>
                </div>
              )}
            </div> */}
          </Col>
        </Row>

        <div style={{ marginBottom: "16px" }}>
          <Row>
            <Col sm={12}>
              <div
                style={{
                  borderRadius: "16px",
                  border: "1px solid #ebebeb",
                  backgroundColor: "#ffffff",
                  overflow: "hidden",
                }}
                className="table-scroll-container"
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                {/* Header */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    borderBottom: "1px solid #ebebeb",
                  }}
                >
                  <div
                    style={{
                      width: "72px",
                      padding: "10px 24px",
                      flexShrink: 0,
                      borderRight: "1px solid #ebebeb",
                    }}
                  >
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      disabled={quotationList.filter((q) => q.STATUS === "Draft").length === 0}
                    />
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: columnsGridTemplate, flex: 1 }}>
                    {visibleOptions.map((label) => (
                      <div
                        key={label}
                        onClick={() => handleSortClick(label)}
                        title={`Sort by ${label}`}
                        style={{
                          minWidth: 0,
                          padding: "10px 16px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          borderRight: "1px solid #ebebeb",
                          cursor: "pointer",
                          userSelect: "none",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: "4px", minWidth: 0 }}>
                          <div
                            className="fs-12 fw-600 text-ltgrey"
                            style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                          >
                            {label}
                          </div>
                          <span className="fs-12 fw-600" style={{ flexShrink: 0, color: sortColumn === label ? "#3D4AF5" : "#c4c9d4" }}>
                            {sortColumn === label && sortDirection === "desc" ? "▼" : "▲"}
                          </span>
                        </div>
                        <img src={Dots} alt="dots" style={{ flexShrink: 0 }} onClick={(e) => e.stopPropagation()} />
                      </div>
                    ))}
                  </div>

                  <div
                    onClick={() => setShowModal(true)}
                    style={{
                      width: "72px",
                      flexShrink: 0,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {/* Customize Icon (Header) */}
                    <img src={Customize} alt="customize" />
                  </div>
                </div>

                {/* Data Rows */}
                {listLoading ? (
                  <div style={{ width: "100%", padding: "40px 0" }}>
                    <Loader height="120px" />
                  </div>
                ) : sortedQuotationList.length === 0 ? (
                  <div style={{ width: "100%", textAlign: "center", padding: "40px 0", color: "#8790A1" }}>
                    No quotations found
                  </div>
                ) : (
                  sortedQuotationList.map((lead, rowIndex) => (
                  <div
                    key={lead.id}
                    onMouseEnter={() => setHoveredIndex(rowIndex)}
                    onMouseLeave={() => setHoveredIndex(null)}
                    style={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      borderBottom: "1px solid #ebebeb",
                      backgroundColor: "#ffffff",
                    }}
                  >
                    <div
                      style={{
                        width: "72px",
                        padding: "10px 24px",
                        flexShrink: 0,
                        borderRight: "1px solid #ebebeb",
                      }}
                    >
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selectedRows.includes(normalizeId(lead.id))}
                        onChange={() => toggleRow(lead.id)}
                        disabled={!anyColumnSelected || lead.STATUS !== "Draft"}
                      />
                    </div>
                    <div
                      style={{ display: "grid", gridTemplateColumns: columnsGridTemplate, flex: 1, cursor: "pointer" }}
                      onClick={() => {
                        // Validate ID before navigation to prevent injection
                        if (
                          lead.id &&
                          String(lead.id).match(/^[a-zA-Z0-9_-]+$/)
                        ) {
                          navigate(
                            `/quotation/view/${encodeURIComponent(lead.id)}`,
                          );
                        }
                      }}
                    >
                      {visibleOptions.map((label) => (
                          <div
                            key={label}
                            title={lead[label]}
                            className="text-dkblue fs-14 fw-400"
                            style={{
                              minWidth: 0,
                              padding: "10px 16px",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              borderRight: "1px solid #ebebeb",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: "8px",
                            }}
                          >
                            <div style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {label === "STATUS"  ? (
                                lead.is_expired===false? (<div
                                  style={{
                                    borderRadius: "8px",
                                    backgroundColor:
                                      lead[label] === "Approved"
                                        ? "#d9ffe9"
                                        : lead[label] === "Rejected"
                                          ? "#FDF0F0"
                                          : "#FFEDE0",
                                    height: "28px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    padding: "0 8px",
                                  }}
                                >
                                  <span
                                    className={`fs-12 fw-600 ${
                                      lead[label] === "Approved"
                                        ? "text-green"
                                        : lead[label] === "Rejected"
                                          ? "textRed"
                                          : "textOrg"
                                    }`}
                                  >
                                    {sanitizeInput(lead[label]) || "-"}
                                  </span>
                                </div>):(
                                  <div
                                  style={{
                                    borderRadius: "8px",
                                    backgroundColor: "#FFEDE0",
                                    height: "28px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    padding: "0 8px",
                                  }}
                                >
                                  <span
                                    className={`fs-12 fw-600 textOrg`}
                                  >
                                    Expired
                                  </span>
                                </div>
                                )
                              ) : label === "AMOUNT" ? (
                                `₹ ${sanitizeInput(String(lead[label] || "-"))}`
                              ) : (
                                sanitizeInput(String(lead[label] || "-"))
                              )}
                            </div>
                            {label === "QUOTATION NO" && lead[label] && (
                              <img
                                src={Copy}
                                alt="Copy"
                                style={{
                                  width: "16px",
                                  height: "16px",
                                  cursor: "pointer",
                                  flexShrink: 0,
                                  opacity: 0.6,
                                  transition: "opacity 0.2s",
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCopyQuotationNo(lead[label]);
                                }}
                                onMouseEnter={(e) => (e.target.style.opacity = "1")}
                                onMouseLeave={(e) => (e.target.style.opacity = "0.6")}
                                title="Copy quotation number"
                              />
                            )}
                          </div>
                      ))}
                    </div>
                    <div style={{ width: "72px", flexShrink: 0 }} />
                    {hoveredIndex === rowIndex && (
                      <div
                        style={{
                          position: "absolute",
                          top: "50%",
                          right: "12px",
                          transform: "translateY(-50%)",
                          zIndex: 1,
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center" }}>
                          {(lead.STATUS === "Draft" || lead.is_expired) && hasEditPermission && (
                            <div
                              onClick={(e) => {
                                e.stopPropagation();
                                // Validate ID before navigation
                                if (
                                  lead.id &&
                                  String(lead.id).match(/^[a-zA-Z0-9_-]+$/)
                                ) {
                                  navigate("/quotation/create", {
                                    state: { id: lead.id },
                                  });
                                }
                              }}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                border: "0.6px solid #E5E5E5",
                                cursor: "pointer",
                                justifyContent: "center",
                                width: "32px",
                                height: "32px",
                                borderRadius: "100px",
                                background: "#FFFFFF",
                              }}
                              title="Edit Quotation"
                            >
                              <EditRow />
                            </div>
                          )}
                          <span style={{ marginLeft: "16px" }}></span>
                          {lead.STATUS === "Draft" && hasDeletePermission && (
                            <div
                              onClick={(e) => {
                                e.stopPropagation();
                                // Validate ID before deletion
                                if (
                                  lead.id &&
                                  String(lead.id).match(/^[a-zA-Z0-9_-]+$/)
                                ) {
                                  setDeleteId(lead.id);
                                }
                              }}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                border: "0.6px solid #E5E5E5",
                                cursor: "pointer",
                                justifyContent: "center",
                                width: "32px",
                                height: "32px",
                                borderRadius: "100px",
                                background: "#FFFFFF",
                              }}
                            >
                              <DeleteRow />
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  ))
                )}

                {showModal && (
                  <div
                    style={{
                      position: "fixed",
                      top: 0,
                      left: 0,
                      width: "100vw",
                      height: "100vh",
                      backgroundColor: "rgba(0,0,0,0.5)",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      zIndex: 1000,
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: "#fff",
                        padding: "24px",
                        borderRadius: "12px",
                        boxShadow: "0px 4px 12px rgba(0,0,0,0.2)",
                        width: "300px",
                        maxHeight: "80vh",
                        overflowY: "auto",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: "600",
                          fontSize: "16px",
                          marginBottom: "16px",
                        }}
                      >
                        Select Columns
                      </div>
                      {options.map((label, index) => (
                        <label
                          key={index}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            marginBottom: "10px",
                            fontSize: "14px",
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={!!selectedColumns[label]}
                            onChange={() => toggleColumn(label)}
                            style={{ marginRight: "8px" }}
                          />
                          {label}
                        </label>
                      ))}
                      <button
                        onClick={() => setShowModal(false)}
                        style={{
                          marginTop: "16px",
                          width: "100%",
                          padding: "10px",
                          borderRadius: "8px",
                          border: "none",
                          backgroundColor: "#3D4AF5",
                          color: "#fff",
                          fontWeight: "500",
                          cursor: "pointer",
                        }}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                )}
                </div>
              </div>
            </Col>
          </Row>
        </div>

        <div>
          <Row>
            <Col>
              <RecordsPerPage
                value={itemsPerPage}
                onChange={(event) => {
                  const value = parseInt(event.target.value, 10);
                  // Validate to prevent DoS attacks
                  if (value > 0 && value <= MAX_ITEMS_PER_PAGE) {
                    setItemsPerPage(value);
                  }
                }}
                labelClassName="text-dkblue fs-13 fw-500"
                selectClassName="select3 fs-14 fw-500 text-dkblue"
                selectStyle={{
                  width: "53px",
                  height: "32px",
                }}
              />
            </Col>
            <Col>
              <div className="d-flex justify-content-end">
                <ReactPaginate
                  previousLabel={
                    <img src={lar} className="ar3" alt="previous" />
                  }
                  nextLabel={<img src={rar} className="ar3" alt="next" />}
                  breakLabel={"..."}
                  pageCount={pageCount + 1}
                  marginPagesDisplayed={2}
                  pageRangeDisplayed={3}
                  onPageChange={handlePageClick}
                  forcePage={itemOffset}
                  containerClassName={"pagination"}
                  activeClassName={"active"}
                />
              </div>
            </Col>
          </Row>

          <ToastContainer position="top-end">
            <Toast
              className="d-inline-block m-1"
              bg={snackbarStatus === true ? "success" : "danger"}
              show={showSnackbar}
              onClose={handleSnackbarClose}
              delay={3000}
              autohide
            >
              <Toast.Body style={{ color: "#ffffff" }}>
                {snackbarMessage}
              </Toast.Body>
            </Toast>
          </ToastContainer>
        </div>
      <AiChat
        askApi={askQuotationChatApi}
        providersApi={getQuotationAiProvidersApi}
        title="Ask about quotations"
        hint="Try: “how many quotations are still pending?” or “break down quotations by status”"
      />
      </div>
    </div>
  );
};

export default QuotationList;
